import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft,
  Plus,
  Save,
  Trash2,
  Edit,
  Loader2,
  DollarSign,
  Clock,
  Sparkles,
  GripVertical,
  Eye,
  EyeOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConsultationManager = ({ user }) => {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: '60',
    price: '',
    active: true
  });

  useEffect(() => {
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/consultations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConsultations(data.consultations || []);
      }
    } catch (error) {
      console.error('Error fetching consultations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (consultation) => {
    setEditingId(consultation.id);
    setFormData({
      name: consultation.name,
      description: consultation.description,
      duration: consultation.duration.replace(' min', ''),
      price: consultation.price.toString(),
      active: consultation.active !== false
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      duration: '60',
      price: '',
      active: true
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price) {
      alert('Name and price are required');
      return;
    }

    setSaving(true);
    try {
      const consultationData = {
        name: formData.name,
        description: formData.description,
        duration: `${formData.duration} min`,
        price: parseFloat(formData.price),
        active: formData.active
      };

      let response;
      if (editingId) {
        // Update existing
        response = await fetch(`${BACKEND_URL}/api/admin/consultations/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(consultationData)
        });
      } else {
        // Create new
        response = await fetch(`${BACKEND_URL}/api/admin/consultations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(consultationData)
        });
      }

      if (response.ok) {
        setSuccessMessage(editingId ? 'Consultation updated!' : 'Consultation created!');
        setTimeout(() => setSuccessMessage(''), 3000);
        handleCancel();
        fetchConsultations();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving consultation:', error);
      alert('Failed to save consultation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this consultation type?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/consultations/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccessMessage('Consultation deleted!');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchConsultations();
      }
    } catch (error) {
      console.error('Error deleting consultation:', error);
    }
  };

  const handleToggleActive = async (consultation) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/consultations/${consultation.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...consultation,
          active: !consultation.active
        })
      });

      if (response.ok) {
        fetchConsultations();
      }
    } catch (error) {
      console.error('Error toggling consultation:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Manage Consultations</h1>
              <p className="text-gray-400 text-sm">Edit consultation types, prices, and descriptions</p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400">
            {successMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-1">
            <Card className="bg-gray-900/50 border-gray-800 sticky top-24">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  {editingId ? <Edit className="w-5 h-5 text-amber-400" /> : <Plus className="w-5 h-5 text-amber-400" />}
                  {editingId ? 'Edit Consultation' : 'Add New Consultation'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="BaZi Life Reading"
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Comprehensive birth chart analysis..."
                    rows={3}
                    className="mt-1 bg-gray-800 border-gray-700 text-white resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Price ($) *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="297"
                        className="pl-9 bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-300">Duration (min)</Label>
                    <div className="relative mt-1">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        type="number"
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        placeholder="60"
                        className="pl-9 bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Label className="text-gray-300">Active</Label>
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  {editingId && (
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 bg-amber-600 hover:bg-amber-500"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Consultation List */}
          <div className="lg:col-span-2 space-y-4">
            {consultations.length === 0 ? (
              <Card className="bg-gray-900/50 border-gray-800">
                <CardContent className="p-8 text-center">
                  <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">No consultations yet</h3>
                  <p className="text-gray-400">Create your first consultation type using the form on the left.</p>
                </CardContent>
              </Card>
            ) : (
              consultations.map((consultation, index) => (
                <Card 
                  key={consultation.id} 
                  className={`bg-gray-900/50 border-gray-800 transition-all ${
                    editingId === consultation.id ? 'ring-2 ring-amber-500' : ''
                  } ${!consultation.active ? 'opacity-60' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex items-center text-gray-600">
                        <GripVertical className="w-5 h-5" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-white">{consultation.name}</h3>
                              {!consultation.active && (
                                <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                                  Hidden
                                </span>
                              )}
                            </div>
                            <p className="text-gray-400 text-sm mt-1">{consultation.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-amber-400">${consultation.price}</p>
                            <p className="text-gray-500 text-sm">{consultation.duration}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleActive(consultation)}
                              className={`flex items-center gap-1 px-3 py-1 rounded text-sm transition-colors ${
                                consultation.active 
                                  ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' 
                                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                              }`}
                            >
                              {consultation.active ? (
                                <>
                                  <Eye className="w-4 h-4" />
                                  Visible
                                </>
                              ) : (
                                <>
                                  <EyeOff className="w-4 h-4" />
                                  Hidden
                                </>
                              )}
                            </button>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(consultation)}
                              className="border-gray-700 text-gray-300 hover:bg-gray-800"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(consultation.id)}
                              className="border-red-900 text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationManager;
